import React, { useState, useEffect } from "react";

// Components
import {
  Header,
  FormWrapper,
  DropdownWrapper,
  Label,
  LogoUploadWrapper,
  HeaderText,
  RedText,
  ServiceCodeSelectWrapper,
  CreateButtonWrapper,
} from "./OrganizationCarriersCreatePage.styles";
import { Select, Button, Tooltip, notification } from "antd";
import { PlusOutlined, InfoCircleOutlined } from "@ant-design/icons";
import UploadLogo from "../../../../components/UploadLogo";
import FulfillmentBackButton from "../../../fulfillment/FulfillmentBackButton";
import InputWithVerification from "../OrganizationUserCreateEditPage/InputWithVerification";

// Helpers
import { get } from "lodash-es";
import { objectToFormData } from "object-to-formdata";

// Hooks
import { useParams, useHistory } from "react-router";
import { useCarriers } from "../../../../hooks/api/fulfillment/carriers";

interface CreateCarrierBody {
  organization_id: string;
  external_id: string;
  carrier_code: string;
  name: string;
  external_platform: string;
  logo_attributes: {
    file: File | string;
  };
  enabled_service_codes: string[];
}

const OrganizationCarriersCreatePage = () => {
  const [name, setName] = useState("");
  const [carrierCode, setCarrierCode] = useState("");
  const [externalPlatform, setExternalPlatform] = useState<string | null>("");
  const [externalCarrierID, setExternalCarrierID] = useState<string | null>("");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [uploadLogoLink, setUploadLogoLink] = useState("");
  const [serviceCodes, setServiceCodes] = useState<string[]>([]);
  const [inputError, setInputError] = useState("");
  const params = useParams();
  const history = useHistory();
  const { carriers, fetchCarriers, createCarrier, updateCarrier } = useCarriers();
  const organizationID = get(params, "organization_id", "");
  const carrierID = get(params, "carrier_id");
  const error = carriers.error || inputError;

  const getCarrier = async () => {
    const carriers = await fetchCarriers(organizationID);
    const carrier = carriers.find((carrier) => carrier.id === carrierID);
    if (carrier) {
      const { name, carrier_code, external_platform, external_id, enabled_service_codes, logo } = carrier;
      setName(name);
      setCarrierCode(carrier_code);
      setExternalPlatform(external_platform);
      setExternalCarrierID(external_id);
      setServiceCodes(enabled_service_codes);
      setUploadLogoLink(logo?.url);
    } else {
      setInputError("Carrier not found.");
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    if (carrierID) getCarrier();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (error) {
      notification.error({
        message: "Error",
        description: error,
      });
    }
  }, [error, inputError]);

  useEffect(() => {
    setInputError("");
  }, [name, carrierCode, externalPlatform, externalCarrierID, imageFile, uploadLogoLink, serviceCodes]);

  const validate = (input: File | string | null) => {
    switch (input) {
      case name:
        return name.length >= 2 && name.length < 256;
      case carrierCode:
        return carrierCode.length >= 2 && carrierCode.length < 256;
      case externalCarrierID:
        if (externalCarrierID) {
          return externalCarrierID?.length >= 2 && externalCarrierID?.length < 256;
        }
        return false;
      case imageFile:
      case uploadLogoLink:
        return imageFile || uploadLogoLink ? true : false;
      default:
        return false;
    }
  };

  const renderExternalPlatformDropdown = () => {
    const externalPlatforms = [
      { name: "None", value: "none" },
      { name: "ShipEngine", value: "shipengine" },
    ];

    const options = externalPlatforms.map((externalPlatform, index) => {
      const { name, value } = externalPlatform;
      return (
        <Select.Option key={index} value={value}>
          {name}
        </Select.Option>
      );
    });

    return (
      <DropdownWrapper>
        <Label>External Platform</Label>
        <Select
          disabled={carrierID ? true : false}
          value={externalPlatform || "Select External Platform..."}
          onChange={(value) => setExternalPlatform(value)}
        >
          {options}
        </Select>
      </DropdownWrapper>
    );
  };

  const headerText = (
    <HeaderText>
      Upload Logo<RedText>*</RedText>
    </HeaderText>
  );

  const renderServiceCodeSelection = () => {
    return (
      <ServiceCodeSelectWrapper>
        <Label>
          Service Codes{" "}
          <Tooltip title="You can type your service code and save it by pushing enter, typing a comma, or clicking a selection.">
            <InfoCircleOutlined />
          </Tooltip>
        </Label>
        <Select
          mode="tags"
          style={{ width: "100%" }}
          onChange={(serviceCodes: string[]) => {
            setServiceCodes(serviceCodes);
          }}
          tokenSeparators={[","]}
          value={serviceCodes}
        >
          {serviceCodes}
        </Select>
      </ServiceCodeSelectWrapper>
    );
  };

  const onSubmit = () => {
    if (!validate(name)) return setInputError("The carrier name must be between 2 and 256 characters.");
    if (!validate(carrierCode)) return setInputError("The carrier code must be between 2 and 256 characters.");
    if (!validate(externalCarrierID))
      return setInputError("The external carrier ID must be between 2 and 256 characters.");
    if (!validate(imageFile) && !validate(uploadLogoLink))
      return setInputError("Please upload a carrier logo or provide an image link.");

    const body: CreateCarrierBody = {
      organization_id: organizationID,
      external_id: externalCarrierID || "",
      carrier_code: carrierCode,
      name,
      external_platform: externalPlatform || "",
      logo_attributes: {
        file: imageFile || uploadLogoLink,
      },
      enabled_service_codes: serviceCodes,
    };

    const formDataBody = objectToFormData(body);

    if (carrierID) {
      updateCarrier(carrierID, formDataBody).then(() => {
        history.push(`/organizations/${organizationID}/carriers`);
      });
    } else {
      createCarrier(formDataBody).then(() => {
        history.push(`/organizations/${organizationID}/carriers`);
      });
    }
  };

  const renderSubmitButton = () => {
    return (
      <CreateButtonWrapper>
        <Button className="create-user-btn" onClick={onSubmit} loading={carriers.loading} icon={<PlusOutlined />}>
          {carrierID ? "Update Carrier" : "Create Carrier"}
        </Button>
      </CreateButtonWrapper>
    );
  };

  return (
    <div className="page-container">
      <FulfillmentBackButton onClick={() => history.goBack()} label="Carriers" />

      <Header>Add Carrier</Header>

      <FormWrapper>
        <InputWithVerification
          value={name}
          setValue={setName}
          isVerified={validate(name)}
          label={
            <HeaderText>
              Carrier Name<RedText>*</RedText>
            </HeaderText>
          }
          labelText="Carrier Name"
        />
        <InputWithVerification
          disabled={carrierID ? true : false}
          value={carrierCode}
          setValue={setCarrierCode}
          isVerified={validate(carrierCode)}
          label={
            <HeaderText>
              Carrier Code<RedText>*</RedText>
            </HeaderText>
          }
          labelText="Carrier Code"
        />
        {renderExternalPlatformDropdown()}
        <InputWithVerification
          disabled={carrierID ? true : false}
          value={externalCarrierID || ""}
          setValue={setExternalCarrierID}
          isVerified={validate(externalCarrierID)}
          label={
            <HeaderText>
              External Carrier ID<RedText>*</RedText>
            </HeaderText>
          }
          labelText="External Carrier ID"
        />
        <LogoUploadWrapper>
          <UploadLogo
            disabled={carrierID ? true : false}
            imageFile={imageFile}
            setImageFile={(file: File | null) => {
              setImageFile(file);
            }}
            uploadLogoLink={uploadLogoLink || ""}
            setUploadLogoLink={(link) => {
              setUploadLogoLink(link);
            }}
            headerText={headerText}
            allowURLUpload
          />
        </LogoUploadWrapper>
        {renderServiceCodeSelection()}
        {renderSubmitButton()}
      </FormWrapper>
    </div>
  );
};

export default OrganizationCarriersCreatePage;
